import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ListIllustration } from "vega-illustrations";

import { useCloseDealOpportunities } from "@vega-api/generated/coinvest-deal-opportunity-actions/coinvest-deal-opportunity-actions";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { type EmptyCardProps } from "@vega-core/components/Cards/EmptyCard/EmptyCard";
import { DataGrid } from "@vega-core/components/DataGrid/DataGrid";
import { useRowSelection } from "@vega-core/components/DataGrid/useRowSelection";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { useCanEditDealOpportunity } from "../../hooks/useCanEditDealOpportunity";
import { useExportFilename } from "../../hooks/useExportFilename";
import { useTableExport } from "../../hooks/useTableExport";
import { BulkCloseDealModal } from "../TrackingTable/components/BulkCloseDealModal";
import { ConfirmNdaSignedModal } from "../TrackingTable/components/ConfirmNdaSignedModal";

import { TrackProcessToolbar } from "./components/TrackProcessToolbar";
import { useTrackProcessColumns } from "./hooks/useTrackProcessColumns";
import { useTrackProcessEdit } from "./hooks/useTrackProcessEdit";

type TrackProcessTableProps = {
  data: DealTrackingLimitedPartnerBreakdown[];
  isLoading?: boolean;
  dealId: string;
};

export const TrackProcessTable = ({
  data,
  isLoading = false,
  dealId,
}: TrackProcessTableProps) => {
  const { canEditDealOpportunity, isMutationLocked } =
    useCanEditDealOpportunity(dealId);

  const [searchQuery, setSearchQuery] = useState("");

  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();

  const {
    isOpen: isBulkCloseDealModalOpen,
    setTrue: openBulkCloseDealModal,
    setFalse: closeBulkCloseDealModal,
  } = useBoolean(false);

  const { mutate: bulkCloseDeal, isPending: isBulkClosing } =
    useCloseDealOpportunities();

  // Created before the filter config so `filtersConfig.disabled` can read
  // `showSelectedOnly`; the visible rows are supplied per-call below.
  const {
    rowSelection,
    getDisplayRows,
    onSelectedRowsChange,
    showSelectedOnly,
    selectedIds,
    selectedCount,
    clearSelection,
    renderShowSelectedOnlyToggle,
  } = useRowSelection<DealTrackingLimitedPartnerBreakdown>({
    getRowId: (row) => row.dealOpportunityId,
    tableId: dealId,
  });

  const handleBulkCloseDeal = () => {
    bulkCloseDeal(
      { data: { dealOpportunityIds: selectedIds } },
      {
        onSuccess: () => {
          closeBulkCloseDealModal();
          clearSelection();
          showToast({
            variant: "success",
            title: m.coInvestments_tracking_dealsClosedSuccessfully({
              count: selectedCount.toString(),
            }),
          });
          queryClient.invalidateQueries({
            queryKey: getGetDealTrackingForGpQueryKey(dealId),
          });
        },
        onError: (error) => {
          showErrorToast(error, {
            defaultMessage: m.coInvestments_tracking_closeDeal_errorMessage(),
          });
        },
      },
    );
  };

  const { columnDefs, renderColumnToggle, onCellKeyDown, exportColumns } =
    useTrackProcessColumns({
      dealId,
      canEditDealOpportunity,
      isMutationLocked,
    });

  const { handleCellValueChanged, confirmNdaSignedModalProps } =
    useTrackProcessEdit(dealId);

  const stageOptions = useMemo(
    () =>
      Array.from(
        new Set(
          data
            .map((row) => row.stage?.trim())
            .filter((stage): stage is string => isTruthy(stage)),
        ),
      )
        .sort((a, b) => a.localeCompare(b))
        .map((stage) => ({ value: stage, label: stage })),
    [data],
  );

  // Retained on Process even though Allocation dropped it (VD-27964).
  const tierOptions = useMemo(
    () =>
      Array.from(new Set(data.map((row) => row.tier).filter(isTruthy)))
        .sort((a, b) => Number(a) - Number(b))
        .map((tier) => ({ value: tier, label: tier })),
    [data],
  );

  const filtersConfig = useMemo(
    () =>
      ({
        filters: [
          {
            id: "stage",
            type: FilterType.Dropdown,
            label: m.coInvestments_tracking_stage(),
            props: {
              placeholder: m.coInvestments_tracking_stage_placeholder({
                value: m.common_all(),
              }),
              renderValue: (label: string) =>
                m.coInvestments_tracking_stage_withValue({ label }),
              options: stageOptions,
              className: "w-56",
            },
          },
          {
            id: "tier",
            type: FilterType.Dropdown,
            label: m.coInvestments_tracking_tier(),
            props: {
              placeholder: m.coInvestments_tracking_tier_placeholder({
                value: m.common_all(),
              }),
              renderValue: (label: string) =>
                m.coInvestments_tracking_tier_withValue({ label }),
              options: tierOptions,
              className: "w-24",
            },
          },
        ],
        maxShownFilters: 2,
        showResetButton: true,
        noMarginBottom: true,
        disabled: showSelectedOnly,
      }) as const satisfies TableFiltersConfig,
    [stageOptions, tierOptions, showSelectedOnly],
  );

  const { filterState, hasActiveFilters, RenderFilters } = useTableFilters({
    id: TableFilterId.CoInvestmentsProcess,
    config: filtersConfig,
    persistInSession: false,
    syncWithURL: false,
  });

  const filteredData = useMemo(() => {
    const stageFilter = filterState.stage?.value;
    const tierFilter = filterState.tier?.value;
    const search = searchQuery.trim().toLowerCase();

    return data.filter((row) => {
      if (search && !row.limitedPartnerName.toLowerCase().includes(search))
        return false;

      if (stageFilter && row.stage?.trim() !== stageFilter) return false;

      if (tierFilter && row.tier !== tierFilter) return false;

      return true;
    });
  }, [data, filterState, searchQuery]);

  const isFiltered = hasActiveFilters || isTruthy(searchQuery);

  const displayRows = getDisplayRows(filteredData);

  const exportFilename = useExportFilename(dealId, "tracking");
  const { exportToCsv, exportToExcel, isExporting } = useTableExport({
    data: displayRows,
    columns: exportColumns,
    filename: exportFilename,
  });

  const gridEmptyState: EmptyCardProps = useMemo(
    () =>
      isFiltered
        ? { title: m.coInvestments_tracking_noFilterResults() }
        : {
            title: m.coInvestments_tracking_noDataTitle(),
            illustration: <ListIllustration />,
            description: (
              <>
                {m.trackingtable_emptyState_uploadPrefix()}{" "}
                <Link
                  className="text-text/secondary underline"
                  data-testid="go-to-prospects-button"
                  to={CoInvestmentFullPaths.CoInvestmentShare.replace(
                    ":id",
                    dealId,
                  )}
                >
                  {m.trackingtable_emptyState_prospectsLinkText()}
                </Link>{" "}
                {m.trackingtable_emptyState_uploadSuffix()}
              </>
            ),
          },
    [isFiltered, dealId],
  );

  return (
    <>
      {selectedCount > 0 ? (
        <BulkActions
          isLoading={isBulkClosing}
          onClear={clearSelection}
          primaryAction={{
            children: m.coInvestments_tracking_closeDeals(),
            onClick: openBulkCloseDealModal,
          }}
          selectedCount={selectedCount}
          selectionLabel={{ single: m.deal_deal(), multiple: m.deal_deals() }}
        />
      ) : null}
      <h2 className="heading-5 text-text/primary mb-4">
        {m.coInvestments_tracking_limitedPartners()}
      </h2>
      <div className="flex flex-col gap-4 mb-4">
        {renderShowSelectedOnlyToggle({
          onShow: () => setSearchQuery(""),
        })}
        <TrackProcessToolbar
          isExporting={isExporting}
          onExportCsv={exportToCsv}
          onExportExcel={exportToExcel}
          onSearchQueryChange={setSearchQuery}
          renderColumnToggle={renderColumnToggle}
          renderFilters={RenderFilters}
          searchDisabled={showSelectedOnly}
          searchQuery={searchQuery}
        />
      </div>
      <div data-testid="track-process-table">
        <DataGrid<DealTrackingLimitedPartnerBreakdown>
          columnDefs={columnDefs}
          emptyState={gridEmptyState}
          getRowId={(params) => params.data.dealOpportunityId}
          loading={isLoading}
          onCellKeyDown={onCellKeyDown}
          onCellValueChanged={handleCellValueChanged}
          onSelectedRowsChange={(next) =>
            onSelectedRowsChange(next, filteredData)
          }
          rowData={displayRows}
          rowSelectionOptions={{
            isRowSelectable: (node) =>
              canEditDealOpportunity &&
              !!node.data &&
              !node.data.isClosed &&
              !node.data.isRejected &&
              !!node.data.commitmentAmount,
          }}
          selectedRows={rowSelection}
        />
      </div>

      <BulkCloseDealModal
        isLoading={isBulkClosing}
        isOpen={isBulkCloseDealModalOpen}
        onClose={closeBulkCloseDealModal}
        onConfirm={handleBulkCloseDeal}
        selectedCount={selectedCount}
      />
      <ConfirmNdaSignedModal {...confirmNdaSignedModalProps} />
    </>
  );
};
